/**
 * High-intent landing page content for the /lp/* hybrid landing set.
 *
 * SCOPE: tightly scoped switchgear + LATAM pages — brand-anchored or
 * use-case-anchored. These are intentionally NOT linked from primary
 * navigation; they exist as conversion-focused entry points for paid +
 * organic search.
 *
 * HARD RULES (do not relax):
 *  - Every page MUST mention switchgear / breakers / control gear and
 *    LATAM delivery + DDP pricing.
 *  - No generic "electrical components" copy. No catalog framing.
 *  - Primary CTA is always "Request Quote" / "Get Pricing".
 *
 * Add a new entry here, register the slug in App.tsx, and rebuild the
 * sitemap to ship a new page — no template changes required.
 */

export type LandingPageKind = "brand" | "use-case" | "product-family";

export type LandingPageContent = {
  /** Slug under /lp/, e.g. "siemens-switchgear-latam". */
  slug: string;
  /** Brand vs use-case anchor — drives the eyebrow + structured-data type. */
  kind: LandingPageKind;
  /** SEO meta title (50–60 chars, keyword-led). */
  metaTitle: string;
  /** SEO meta description (140–160 chars). */
  metaDescription: string;
  /** Eyebrow above H1. */
  eyebrow: string;
  /** H1 — must contain the primary keyword + LATAM cue. */
  h1: string;
  /** Subheadline emphasizing speed + landed pricing. */
  subheadline: string;
  /** "Use case" / scenario explanation — 1–3 sentences. */
  useCase: { title: string; body: string };
  /** Product / category context — bulleted families + supported brands. */
  context: {
    title: string;
    intro: string;
    items: { name: string; detail?: string }[];
  };
  /** Three short pain points the page resolves. */
  pains: string[];
  /** Final CTA copy. */
  finalCta: { title: string; body: string };
  /** Breadcrumb label. */
  breadcrumbLabel: string;
};

/** Re-usable building blocks to keep brand/use-case copy consistent. */
const LATAM_COUNTRIES = "Colombia, Mexico, Peru, Chile, Brazil, and across LATAM";

const BRAND_PRODUCT_LINES: Record<
  "siemens" | "schneider" | "eaton" | "abb",
  { name: string; detail: string }[]
> = {
  siemens: [
    { name: "Sentron 3VA / 3VL MCCBs", detail: "100–1600 A, 3- and 4-pole" },
    { name: "Sirius contactors & overloads", detail: "3RT / 3RU / 3RV families" },
    { name: "Sivacon switchgear components", detail: "Bus + protection assemblies" },
    { name: "7SR / 7SJ protection relays", detail: "Feeder + transformer protection" },
  ],
  schneider: [
    { name: "Masterpact ACBs", detail: "MTZ1 / MTZ2 air circuit breakers" },
    { name: "PowerPact MCCBs", detail: "B / H / J / L / R frames" },
    { name: "TeSys contactors & starters", detail: "D, F, K, T families" },
    { name: "Easergy protection relays", detail: "P3 / P5 feeder protection" },
  ],
  eaton: [
    { name: "Magnum DS air circuit breakers", detail: "800–6000 A, drawout" },
    { name: "Series C MCCBs", detail: "F / J / K / L / N / R frames" },
    { name: "Cutler-Hammer aftermarket", detail: "Legacy + obsolete replacements" },
    { name: "Freedom & XT contactors", detail: "Motor control assemblies" },
  ],
  abb: [
    { name: "Tmax T1–T7 / XT MCCBs", detail: "16–1600 A breaker families" },
    { name: "Emax 2 ACBs", detail: "E1.2–E6.2 air circuit breakers" },
    { name: "AF contactors & overloads", detail: "Coil-tolerant motor control" },
    { name: "Relion 615 / 620 protection", detail: "Feeder + bus protection" },
  ],
};

/* ────────────────────────────────────────────────────────────────────── */
/*  BRAND PAGES                                                          */
/* ────────────────────────────────────────────────────────────────────── */

const SIEMENS: LandingPageContent = {
  slug: "siemens-switchgear-latam",
  kind: "brand",
  metaTitle: "Siemens Switchgear & Breakers — DDP to LATAM",
  metaDescription:
    "Source Siemens Sentron MCCBs, Sirius contactors, and switchgear components from U.S. supply with full DDP delivery to LATAM. Quotes in hours.",
  eyebrow: "Siemens · Switchgear · LATAM",
  h1: "Siemens Switchgear & Breakers, Delivered DDP Across LATAM",
  subheadline:
    `U.S. inventory of Sentron, Sirius, and Sivacon switchgear families — quoted with full landed (DDP) pricing and delivered to ${LATAM_COUNTRIES}.`,
  useCase: {
    title: "Why buyers source Siemens switchgear from U.S. supply",
    body:
      "Local Siemens distribution in LATAM often shows 12+ week lead times for Sentron breakers and Sirius motor control. We pull from verified U.S. distribution and aftermarket networks, then run export, customs, and final-mile delivery so the part arrives without your team chasing freight.",
  },
  context: {
    title: "Siemens product families we source",
    intro:
      "Switchgear-grade Siemens components — focused on the SKUs that actually break or get specified for LATAM panels and substations.",
    items: BRAND_PRODUCT_LINES.siemens,
  },
  pains: [
    "Sentron 3VA breakers backordered through local Siemens channels",
    "Sirius motor control SKUs out of stock for plant downtime",
    "Cross-border freight + customs adding weeks to delivery",
  ],
  finalCta: {
    title: "Send the Siemens SKU — get a landed quote in hours",
    body:
      "Submit a part number, photo, or spec sheet. We confirm U.S. stock and return a full DDP price for delivery into your country.",
  },
  breadcrumbLabel: "Siemens Switchgear (LATAM)",
};

const SCHNEIDER: LandingPageContent = {
  slug: "schneider-switchgear-latam",
  kind: "brand",
  metaTitle: "Schneider Electric Switchgear — DDP to LATAM",
  metaDescription:
    "Source Schneider PowerPact MCCBs, Masterpact ACBs, and TeSys contactors from U.S. supply with DDP delivery to LATAM. Firm landed quotes in hours.",
  eyebrow: "Schneider Electric · Switchgear · LATAM",
  h1: "Schneider Electric Switchgear, Sourced From U.S. Supply",
  subheadline:
    `Masterpact ACBs, PowerPact MCCBs, and TeSys motor control — quoted DDP and delivered to ${LATAM_COUNTRIES}.`,
  useCase: {
    title: "Why teams source Schneider switchgear through us",
    body:
      "Schneider's most-specified switchgear lines — PowerPact, Masterpact, TeSys — frequently fall to allocation in LATAM. We locate the SKU across U.S. distribution, lock landed pricing, and coordinate DDP delivery so the project does not slip on a single part.",
  },
  context: {
    title: "Schneider product families we source",
    intro:
      "Switchgear and motor-control assemblies focused on operational replacement and new-build panels.",
    items: BRAND_PRODUCT_LINES.schneider,
  },
  pains: [
    "PowerPact frames showing 'no stock' through local distribution",
    "Masterpact trip units / accessories on extended allocation",
    "TeSys contactors blocking motor control assembly builds",
  ],
  finalCta: {
    title: "Send the Schneider SKU — get availability + DDP price",
    body:
      "Submit the catalog number or a photo. We confirm U.S. stock and return a full landed quote for delivery into your country.",
  },
  breadcrumbLabel: "Schneider Switchgear (LATAM)",
};

const EATON: LandingPageContent = {
  slug: "eaton-switchgear-latam",
  kind: "brand",
  metaTitle: "Eaton Switchgear & Breakers — DDP to LATAM",
  metaDescription:
    "Source Eaton Magnum DS, Series C MCCBs, and Cutler-Hammer aftermarket parts from U.S. supply. Landed (DDP) quotes in hours, delivered to LATAM.",
  eyebrow: "Eaton · Switchgear · LATAM",
  h1: "Eaton Switchgear & Breakers, Delivered to LATAM",
  subheadline:
    `Magnum DS, Series C, and Cutler-Hammer aftermarket components — quoted DDP and delivered to ${LATAM_COUNTRIES}.`,
  useCase: {
    title: "Why we focus on Eaton switchgear for LATAM",
    body:
      "Eaton's installed base in LATAM is heavy on Magnum DS and Series C — including Cutler-Hammer-branded legacy units that local channels no longer prioritize. We source through U.S. distribution and aftermarket, ship DDP, and surface compatible substitutes when the original SKU is gone.",
  },
  context: {
    title: "Eaton product families we source",
    intro:
      "Switchgear, MCCBs, and motor control — including legacy Cutler-Hammer SKUs supported through aftermarket networks.",
    items: BRAND_PRODUCT_LINES.eaton,
  },
  pains: [
    "Magnum DS trip units / accessories blocking switchgear restoration",
    "Series C MCCBs with no local stock for replacement",
    "Cutler-Hammer legacy parts marked obsolete by local distributors",
  ],
  finalCta: {
    title: "Send the Eaton or Cutler-Hammer SKU",
    body:
      "We will confirm U.S. stock — including aftermarket — and return a full DDP quote for delivery into your country.",
  },
  breadcrumbLabel: "Eaton Switchgear (LATAM)",
};

const ABB: LandingPageContent = {
  slug: "abb-switchgear-latam",
  kind: "brand",
  metaTitle: "ABB Tmax & Emax Switchgear — DDP to LATAM",
  metaDescription:
    "Source ABB Tmax MCCBs, Emax 2 ACBs, and AF contactors from U.S. supply. Full landed (DDP) pricing and delivery to LATAM. Quotes in hours.",
  eyebrow: "ABB · Switchgear · LATAM",
  h1: "ABB Tmax, Emax & AF Switchgear, Sourced From U.S. Supply",
  subheadline:
    `Tmax XT MCCBs, Emax 2 ACBs, AF contactors, and Relion protection relays — quoted DDP and delivered to ${LATAM_COUNTRIES}.`,
  useCase: {
    title: "Why teams source ABB switchgear through us",
    body:
      "ABB's Tmax XT and Emax 2 lines are heavily specified across LATAM substations and OEM panels, and local lead times routinely run 8–14 weeks. We pull from U.S. distribution, run customs, and ship DDP so the SKU arrives without backchannel coordination.",
  },
  context: {
    title: "ABB product families we source",
    intro:
      "Switchgear-grade ABB components for substations, MCCs, and OEM panels.",
    items: BRAND_PRODUCT_LINES.abb,
  },
  pains: [
    "Tmax XT frames on extended allocation through local channels",
    "Emax 2 trip units holding up switchgear commissioning",
    "AF contactors stalling motor-control assembly schedules",
  ],
  finalCta: {
    title: "Send the ABB SKU — landed quote in hours",
    body:
      "Catalog number, ordering code, or photo all work. We confirm U.S. availability and return a full DDP price for your destination.",
  },
  breadcrumbLabel: "ABB Switchgear (LATAM)",
};

/* ────────────────────────────────────────────────────────────────────── */
/*  USE-CASE PAGES                                                       */
/* ────────────────────────────────────────────────────────────────────── */

const URGENT: LandingPageContent = {
  slug: "urgent-switchgear-replacement-latam",
  kind: "use-case",
  metaTitle: "Urgent Switchgear Replacement — DDP to LATAM",
  metaDescription:
    "Plant down? Source urgent switchgear and breaker replacements from U.S. supply with DDP delivery across LATAM. Quotes in hours, delivery in days.",
  eyebrow: "Use case · Urgent replacement · LATAM",
  h1: "Urgent Switchgear Replacement, Delivered DDP to LATAM",
  subheadline:
    `When a breaker or switchgear assembly fails and local stock is gone, we pull from U.S. supply and run DDP delivery to ${LATAM_COUNTRIES} in days.`,
  useCase: {
    title: "When this page is the right fit",
    body:
      "A failed MCCB, ACB, or switchgear component is blocking production. Local distribution shows 6–14 weeks. You need the SKU on site this week, not this quarter — with customs and freight already handled.",
  },
  context: {
    title: "Urgent switchgear replacements we source",
    intro:
      "Anchor brands and component types that drive the majority of urgent switchgear requests in LATAM.",
    items: [
      { name: "Siemens Sentron 3VA / 3VL MCCBs", detail: "Replacement frames + trip units" },
      { name: "Schneider PowerPact / Masterpact", detail: "Breakers + accessories" },
      { name: "Eaton Magnum DS / Series C", detail: "ACBs + drawout assemblies" },
      { name: "ABB Tmax XT / Emax 2", detail: "MCCBs + ACBs" },
    ],
  },
  pains: [
    "Production line down, local distribution out of stock",
    "OEM lead time longer than the production budget can absorb",
    "Cross-border freight + customs adding weeks of unknowns",
  ],
  finalCta: {
    title: "Send the failed SKU — quote within hours",
    body:
      "Photo, nameplate, or part number all work. We confirm U.S. stock immediately and return a firm DDP price for delivery to site.",
  },
  breadcrumbLabel: "Urgent Switchgear Replacement",
};

const HARD_TO_FIND: LandingPageContent = {
  slug: "hard-to-find-switchgear-latam",
  kind: "use-case",
  metaTitle: "Hard-to-Find Switchgear Parts — DDP to LATAM",
  metaDescription:
    "Discontinued, allocated, or obsolete switchgear SKUs sourced from verified U.S. distribution and aftermarket. Full DDP delivery across LATAM.",
  eyebrow: "Use case · Hard-to-find · LATAM",
  h1: "Hard-to-Find Switchgear Parts, Sourced From U.S. Supply",
  subheadline:
    `Obsolete trip units, allocated frames, legacy Cutler-Hammer SKUs — located across U.S. distribution + aftermarket and shipped DDP to ${LATAM_COUNTRIES}.`,
  useCase: {
    title: "When this page is the right fit",
    body:
      "The OEM has discontinued the SKU, but your panel still runs on it. Local distribution returns 'no stock' or refuses to quote. You need someone who works the U.S. aftermarket as a primary channel, not as a fallback.",
  },
  context: {
    title: "Hard-to-find categories we source",
    intro:
      "Switchgear and control families where U.S. aftermarket inventory most often fills the gap.",
    items: [
      { name: "Legacy Cutler-Hammer breakers", detail: "Series C / older frames" },
      { name: "Obsolete Siemens 3VL / ITE breakers", detail: "Pre-Sentron families" },
      { name: "Discontinued Schneider PowerPact accessories", detail: "Trip units, modules, lugs" },
      { name: "ABB SACE Tmax T-series legacy", detail: "Pre-Tmax XT frames" },
    ],
  },
  pains: [
    "OEM has discontinued the SKU, no local stock anywhere",
    "Aftermarket sources are fragmented and hard to verify",
    "Customs and freight risk on low-volume aftermarket parts",
  ],
  finalCta: {
    title: "Send the obsolete SKU — we'll work the aftermarket",
    body:
      "Submit any reference (catalog code, photo, panel sticker). We map it to active or aftermarket equivalents and return a DDP quote.",
  },
  breadcrumbLabel: "Hard-to-Find Switchgear",
};

const PLANNED: LandingPageContent = {
  slug: "planned-switchgear-maintenance-latam",
  kind: "use-case",
  metaTitle: "Planned Switchgear Maintenance Sourcing — LATAM DDP",
  metaDescription:
    "Source breaker spares, trip units, and switchgear consumables for planned maintenance windows. DDP delivery to LATAM, on your shutdown date.",
  eyebrow: "Use case · Planned maintenance · LATAM",
  h1: "Planned Switchgear Maintenance Sourcing for LATAM",
  subheadline:
    `Breaker spares, trip units, MCC buckets, and switchgear consumables — sourced from U.S. supply with DDP delivery aligned to your shutdown window in ${LATAM_COUNTRIES}.`,
  useCase: {
    title: "When this page is the right fit",
    body:
      "You have a scheduled outage in 4–10 weeks and need to walk into the window with every spare on site. We consolidate brand-mixed BOMs, lock landed pricing, and stage delivery against your shutdown date.",
  },
  context: {
    title: "Maintenance categories we source",
    intro:
      "Switchgear consumables and spares typically grouped into a single planned-maintenance BOM.",
    items: [
      { name: "Breaker spares & trip units", detail: "Siemens / Schneider / Eaton / ABB" },
      { name: "Contactors & overload relays", detail: "Motor control replacements" },
      { name: "Protection relays & test plugs", detail: "Feeder + transformer protection" },
      { name: "Switchgear hardware kits", detail: "Lugs, terminals, drawout accessories" },
    ],
  },
  pains: [
    "Multi-brand BOM scattered across distributors and lead times",
    "Risk of one missing spare delaying the entire shutdown",
    "Need delivery aligned to a fixed maintenance window",
  ],
  finalCta: {
    title: "Upload your maintenance BOM",
    body:
      "Send the spreadsheet, PDF, or list. We return one consolidated DDP quote with delivery aligned to your shutdown date.",
  },
  breadcrumbLabel: "Planned Switchgear Maintenance",
};

const ATS_BACKUP: LandingPageContent = {
  slug: "ats-backup-switchgear-latam",
  kind: "use-case",
  metaTitle: "ATS & Backup Switchgear Components — DDP to LATAM",
  metaDescription:
    "Source ATS, transfer switches, and backup switchgear from U.S. supply. Full DDP pricing and delivery to LATAM for critical-power upgrades.",
  eyebrow: "Use case · ATS & backup power · LATAM",
  h1: "ATS & Backup Switchgear, Sourced From U.S. Supply",
  subheadline:
    `Automatic transfer switches, transfer-pair breakers, and backup switchgear — quoted DDP and delivered to ${LATAM_COUNTRIES} for critical-power projects.`,
  useCase: {
    title: "When this page is the right fit",
    body:
      "You're standing up or upgrading a critical-power installation — data center, hospital, or utility — and need ATS components plus matched switchgear that local channels can't deliver in time.",
  },
  context: {
    title: "ATS + backup categories we source",
    intro:
      "Critical-power switchgear families specified for ATS and backup installations across LATAM.",
    items: [
      { name: "ASCO 7000 / 4000 ATS series", detail: "Open + closed transition" },
      { name: "Eaton ATC / Magnum transfer pairs", detail: "ACB-paired transfer assemblies" },
      { name: "Schneider Square D / ASCO ATS", detail: "Service-entry transfer switches" },
      { name: "Generator-paired switchgear", detail: "Paralleling + protection assemblies" },
    ],
  },
  pains: [
    "ATS lead times pushing critical-power commissioning back",
    "Mixed-brand transfer + breaker pairs hard to source through one channel",
    "Project needs DDP visibility, not EXW pricing fragments",
  ],
  finalCta: {
    title: "Send your ATS + switchgear BOM",
    body:
      "We'll source the transfer switches and matched breakers together, then return one consolidated DDP quote.",
  },
  breadcrumbLabel: "ATS & Backup Switchgear",
};

/* ────────────────────────────────────────────────────────────────────── */
/*  PRODUCT-FAMILY PAGES                                                 */
/* ────────────────────────────────────────────────────────────────────── */

const CIRCUIT_BREAKERS: LandingPageContent = {
  slug: "circuit-breakers-latam",
  kind: "product-family",
  metaTitle: "Circuit Breakers — DDP to LATAM | MCCB, ACB, MCB",
  metaDescription:
    "Source MCCBs, ACBs, and miniature breakers from Siemens, Schneider, Eaton, and ABB. U.S. supply, full DDP pricing, delivered across LATAM in days.",
  eyebrow: "Product family · Circuit breakers · LATAM",
  h1: "Circuit Breakers, Sourced From U.S. Supply and Delivered DDP",
  subheadline:
    `MCCBs, ACBs, and miniature breakers from Siemens, Schneider, Eaton, and ABB — quoted with full landed pricing and delivered to ${LATAM_COUNTRIES}.`,
  useCase: {
    title: "When you need a breaker — exact or equivalent — fast",
    body:
      "A failed or specified breaker is the single most common blocker on LATAM switchgear projects. We pull the exact frame from U.S. distribution or surface a verified equivalent, then ship DDP so the SKU lands on site instead of stalling at customs.",
  },
  context: {
    title: "Breaker families we source",
    intro:
      "Switchgear-grade MCCB, ACB, and miniature breaker families across the four anchor brands.",
    items: [
      { name: "MCCBs (molded case)", detail: "Sentron, PowerPact, Series C, Tmax XT" },
      { name: "ACBs (air circuit breakers)", detail: "Masterpact, Magnum DS, Emax 2" },
      { name: "Miniature breakers (MCBs)", detail: "DIN-rail single + multi-pole" },
      { name: "Trip units & accessories", detail: "Electronic + thermal-magnetic, drawout kits" },
    ],
  },
  pains: [
    "Specified frame backordered through every local distributor",
    "Trip unit or accessory missing from an otherwise-complete order",
    "Need a verified equivalent because the original SKU is obsolete",
  ],
  finalCta: {
    title: "Send the breaker SKU — landed quote in hours",
    body:
      "Catalog code, photo, or panel sticker. We confirm U.S. stock — exact or equivalent — and return a full DDP price for delivery to your country.",
  },
  breadcrumbLabel: "Circuit Breakers (LATAM)",
};

const CONTACTORS_RELAYS: LandingPageContent = {
  slug: "contactors-relays-latam",
  kind: "product-family",
  metaTitle: "Contactors & Relays — DDP to LATAM | Motor Control",
  metaDescription:
    "Source contactors, overload relays, and motor control devices from Siemens Sirius, Schneider TeSys, Eaton, and ABB AF. Full DDP delivery across LATAM.",
  eyebrow: "Product family · Contactors & relays · LATAM",
  h1: "Contactors & Overload Relays for LATAM Motor Control",
  subheadline:
    `Sirius, TeSys, Freedom/XT, and AF contactor families with matched overload relays — quoted DDP and delivered to ${LATAM_COUNTRIES}.`,
  useCase: {
    title: "When motor control assemblies cannot wait on local stock",
    body:
      "MCC and starter assemblies stall when a single contactor frame or overload relay is missing. We source matched contactor + overload pairs from U.S. supply, including coil-voltage variants, and deliver DDP so the assembly ships on schedule.",
  },
  context: {
    title: "Motor control families we source",
    intro:
      "Contactors, overloads, and starter assemblies across the four anchor brands.",
    items: [
      { name: "Siemens Sirius 3RT / 3RU / 3RV", detail: "Contactors + overloads + breakers" },
      { name: "Schneider TeSys D / F / K / T", detail: "Coil-tolerant contactors + overloads" },
      { name: "Eaton Freedom & XT", detail: "Motor-control assemblies + accessories" },
      { name: "ABB AF contactors & overloads", detail: "Wide-voltage coil + electronic overloads" },
    ],
  },
  pains: [
    "Specific coil voltage out of stock through local channels",
    "Matched contactor + overload pairs scattered across distributors",
    "MCC build delayed by a single overload relay",
  ],
  finalCta: {
    title: "Send the contactor / overload SKU",
    body:
      "Submit the catalog number plus required coil voltage. We confirm U.S. availability and return a full DDP quote for delivery into your country.",
  },
  breadcrumbLabel: "Contactors & Relays (LATAM)",
};

const DISCONNECTS: LandingPageContent = {
  slug: "disconnect-switches-latam",
  kind: "product-family",
  metaTitle: "Disconnect Switches — DDP to LATAM | Safety Switches",
  metaDescription:
    "Source fusible and non-fusible disconnect switches, load-break switches, and safety switches from U.S. supply with full DDP delivery to LATAM.",
  eyebrow: "Product family · Disconnect switches · LATAM",
  h1: "Disconnect & Load-Break Switches, Delivered DDP to LATAM",
  subheadline:
    `Fusible, non-fusible, and load-break disconnect switches from Siemens, Schneider, Eaton, and ABB — quoted with full landed pricing for ${LATAM_COUNTRIES}.`,
  useCase: {
    title: "When safety isolation cannot wait on long lead times",
    body:
      "Disconnects are non-negotiable for safe maintenance and code compliance, but local stock for the right amperage and enclosure rating frequently runs dry. We pull from U.S. distribution and ship DDP so the switch arrives ready to install.",
  },
  context: {
    title: "Disconnect families we source",
    intro:
      "Safety, load-break, and motor-disconnect families across the anchor brands and enclosure ratings.",
    items: [
      { name: "Fusible safety switches", detail: "30–1200 A, NEMA 1 / 3R / 4 / 4X" },
      { name: "Non-fusible disconnects", detail: "Heavy + general duty enclosed switches" },
      { name: "Load-break switches", detail: "On-load isolation for switchgear assemblies" },
      { name: "Motor disconnects", detail: "Local lockable motor-circuit isolators" },
    ],
  },
  pains: [
    "Right amperage + enclosure rating combo not in local stock",
    "Maintenance window blocked by a single isolation switch",
    "Need NEMA 4 / 4X for outdoor or wash-down environments",
  ],
  finalCta: {
    title: "Send the disconnect spec — landed quote in hours",
    body:
      "Submit amperage, poles, fusing, and enclosure rating. We confirm U.S. stock and return a full DDP quote for delivery to your country.",
  },
  breadcrumbLabel: "Disconnect Switches (LATAM)",
};

const ATS_COMPONENTS: LandingPageContent = {
  slug: "ats-components-latam",
  kind: "product-family",
  metaTitle: "ATS Components — DDP to LATAM | Transfer Switches",
  metaDescription:
    "Source automatic transfer switches, controllers, and transfer-pair breakers from ASCO, Eaton, and Schneider. U.S. supply, DDP delivery across LATAM.",
  eyebrow: "Product family · ATS components · LATAM",
  h1: "ATS & Transfer Switch Components for LATAM Critical Power",
  subheadline:
    `Automatic transfer switches, controllers, and transfer-pair breakers — sourced from U.S. supply and delivered DDP to ${LATAM_COUNTRIES}.`,
  useCase: {
    title: "When critical-power commissioning cannot slip",
    body:
      "Data centers, hospitals, and utility installations cannot move forward without ATS hardware on site. We source the transfer switch, controller, and matched breaker pair through U.S. distribution and ship DDP so commissioning stays on date.",
  },
  context: {
    title: "ATS component families we source",
    intro:
      "Critical-power components for ATS, transfer pairs, and paralleling switchgear.",
    items: [
      { name: "ASCO 7000 / 4000 ATS", detail: "Open + closed transition transfer switches" },
      { name: "Eaton ATC controllers + transfer pairs", detail: "Magnum DS-paired assemblies" },
      { name: "Schneider Square D / ASCO ATS", detail: "Service-entry transfer switches" },
      { name: "Bypass-isolation switches", detail: "Maintenance-bypass transfer assemblies" },
    ],
  },
  pains: [
    "ATS lead time pushing critical-power energization back",
    "Controller or accessory missing from an otherwise-complete ATS",
    "Need matched transfer-pair breakers from one channel, not three",
  ],
  finalCta: {
    title: "Send the ATS spec or BOM",
    body:
      "Provide amperage, poles, transition type, and controller. We return a consolidated DDP quote for the full transfer-switch assembly.",
  },
  breadcrumbLabel: "ATS Components (LATAM)",
};

const PROTECTION_DEVICES: LandingPageContent = {
  slug: "protection-devices-latam",
  kind: "product-family",
  metaTitle: "Protection Relays & Devices — DDP to LATAM",
  metaDescription:
    "Source feeder, transformer, and motor protection relays from Siemens 7SR, Schneider Easergy, ABB Relion, and SEL. Full DDP delivery across LATAM.",
  eyebrow: "Product family · Protection devices · LATAM",
  h1: "Protection Relays & Devices, Sourced From U.S. Supply",
  subheadline:
    `Feeder, transformer, motor, and bus protection relays — quoted DDP and delivered to ${LATAM_COUNTRIES} for substation and switchgear projects.`,
  useCase: {
    title: "When protection schemes cannot wait on OEM lead times",
    body:
      "Protection relays are the longest-lead item on most LATAM substation and switchgear projects. We pull from U.S. distribution across Siemens, Schneider, ABB, and SEL — and ship DDP so the relay lands on site without coordination overhead.",
  },
  context: {
    title: "Protection families we source",
    intro:
      "Numerical protection relays and supporting devices across the major substation and switchgear brands.",
    items: [
      { name: "Siemens 7SR / 7SJ / Siprotec", detail: "Feeder + transformer protection" },
      { name: "Schneider Easergy P3 / P5 / MiCOM", detail: "Feeder + bus protection relays" },
      { name: "ABB Relion 615 / 620 / 630", detail: "Feeder + transformer + bus protection" },
      { name: "SEL 351 / 487 / 751", detail: "Feeder, transformer, and bus differential" },
    ],
  },
  pains: [
    "Numerical relay on 16+ week OEM lead time through local channels",
    "Test plug, terminal block, or accessory missing from delivery",
    "Substation energization blocked by a single protection device",
  ],
  finalCta: {
    title: "Send the relay catalog code — landed quote in hours",
    body:
      "Provide the ordering code with options. We confirm U.S. availability and return a full DDP quote for delivery into your country.",
  },
  breadcrumbLabel: "Protection Devices (LATAM)",
};

/* ────────────────────────────────────────────────────────────────────── */
/*  REGISTRY                                                             */
/* ────────────────────────────────────────────────────────────────────── */

export const LANDING_PAGES: Record<string, LandingPageContent> = {
  [SIEMENS.slug]: SIEMENS,
  [SCHNEIDER.slug]: SCHNEIDER,
  [EATON.slug]: EATON,
  [ABB.slug]: ABB,
  [URGENT.slug]: URGENT,
  [HARD_TO_FIND.slug]: HARD_TO_FIND,
  [PLANNED.slug]: PLANNED,
  [ATS_BACKUP.slug]: ATS_BACKUP,
  [CIRCUIT_BREAKERS.slug]: CIRCUIT_BREAKERS,
  [CONTACTORS_RELAYS.slug]: CONTACTORS_RELAYS,
  [DISCONNECTS.slug]: DISCONNECTS,
  [ATS_COMPONENTS.slug]: ATS_COMPONENTS,
  [PROTECTION_DEVICES.slug]: PROTECTION_DEVICES,
};

export const LANDING_PAGE_SLUGS = Object.keys(LANDING_PAGES);

/** Convenience lookup with safe undefined fall-through. */
export function getLandingPage(slug: string | undefined): LandingPageContent | undefined {
  if (!slug) return undefined;
  return LANDING_PAGES[slug];
}